/**
 * src/lib/voice/queueWorker.js — Voice Sessions V-2 worker singleton.
 *
 * Plan: docs/plans/2026-05-01-voice-sessions-and-image-sync-plan.md (V-2).
 *
 * Drains queued `voice_recordings` rows from IndexedDB. Picks up to
 * `concurrency` items at a time, calls the injected `processFn(recording)`
 * to do upload + voice.process_upload, and applies exponential backoff on
 * failure. Failed rows flip back to status='queued' with `next_retry_at`
 * in the future; after MAX_RETRIES they go terminal as status='error'.
 *
 * Leader election: navigator.locks. Only one tab in a multi-tab session
 * runs the loop. Followers see the lock held and bail with
 * `lock-held-elsewhere`. Lock auto-releases on tab close (browser
 * semantics) so the next tab takes over without manual coordination.
 *
 * Public API:
 *   startWorker({ processFn, concurrency, pollMs, lockName })
 *   stopWorker()
 *   tickOnce({ processFn, concurrency, inFlight })   // testable single iteration
 *   isWorkerRunning()
 *   _resetWorkerState()                              // for tests
 *   _internal { backoffMs, isOnBackoff, selectQueued, processOne }
 */

import { getAllRecordings, updateRecording, _resetDB } from '../voiceQueue.js';
import { logVoiceQueueEvent } from './logVoiceQueueEvent.js';
import { isPermanentVoiceError } from './audioGuards.js';

const DEFAULT_LOCK_NAME = 'acn-voice-queue-leader';
const DEFAULT_CONCURRENCY = 2;
const DEFAULT_POLL_MS = 1000;
const MAX_RETRIES = 10;
const BACKOFF_CAP_MS = 60_000;

let stopFlag = false;
let leaderActive = false;
let leaderPromise = null;
const moduleInFlight = new Set();
// Double-fire guard (Slice 4a): recording_ids currently being uploaded. A large
// upload can take >5 min on mobile, so rescueStaleUploading() may flip its row
// back to 'queued' while it's STILL running — without this set the worker would
// start a second concurrent upload of the same recording (the 2026-06-02 incident:
// 6 duplicate multipart uploads + a 400/abort storm). One upload per recording.
const moduleInFlightIds = new Set();

function hasWebLocks() {
  return typeof navigator !== 'undefined'
      && navigator.locks
      && typeof navigator.locks.request === 'function';
}

function backoffMs(attempt) {
  const a = Math.max(attempt, 1);
  return Math.min(2 ** a * 1000, BACKOFF_CAP_MS);
}

function isOnBackoff(rec, now = Date.now()) {
  return typeof rec?.next_retry_at === 'number' && rec.next_retry_at > now;
}

// CEO 2026-05-04 — stale-uploading rescue. If a recording sits in
// 'uploading' for >5 min on the BROWSER side, the worker tab probably
// crashed mid-upload (or the heavy 22MB IDB blob froze Safari). Without
// this rescue the row sits forever and blocks the whole queue.
const STALE_UPLOADING_MS = 5 * 60 * 1000;

async function rescueStaleUploading(now = Date.now()) {
  const all = await getAllRecordings();
  const stale = all.filter(
    (r) => r.status === 'uploading'
        && (now - (r.updated_at || r.created_at || now)) > STALE_UPLOADING_MS,
  );
  for (const r of stale) {
    try {
      // Bump retry_count so we eventually give up if this keeps repeating.
      const attempt = (r.retry_count || 0) + 1;
      const terminal = attempt >= MAX_RETRIES;
      await updateRecording(r.recording_id, {
        status: terminal ? 'error' : 'queued',
        retry_count: attempt,
        next_retry_at: terminal ? null : now + backoffMs(attempt),
        error_message: terminal
          ? 'Stuck uploading >5 min — abandoned after retries'
          : 'Recovered from stale uploading state',
      });
    } catch (err) {
      console.warn('[queueWorker] rescueStaleUploading failed:', err?.message);
    }
  }
  return stale.length;
}

async function selectQueued(now = Date.now()) {
  // Always rescue stale 'uploading' rows BEFORE selecting fresh queue —
  // ensures a stuck recording can't permanently block a healthy tab.
  await rescueStaleUploading(now);
  const all = await getAllRecordings();
  return all
    .filter((r) => r.status === 'queued' && !isOnBackoff(r, now))
    .sort((a, b) => (a.created_at || 0) - (b.created_at || 0));
}

async function processOne(rec, processFn) {
  await updateRecording(rec.recording_id, { status: 'uploading' });
  const startedAt = Date.now();
  logVoiceQueueEvent({
    event: 'upload_start',
    recording_id: rec.recording_id,
    scope_type: rec.scope_type,
    scope_id: rec.scope_id,
  });
  try {
    await processFn(rec);
    logVoiceQueueEvent({
      event: 'upload_ok',
      recording_id: rec.recording_id,
      scope_type: rec.scope_type,
      scope_id: rec.scope_id,
      duration_ms: Date.now() - startedAt,
    });
    return { ok: true };
  } catch (err) {
    const attempt = (rec.retry_count || 0) + 1;
    // TICKET-1079: a failure that is a property of the FILE (Whisper 400
    // "corrupted or unsupported", empty recording) fails identically on every
    // retry — stop now instead of 10 paid attempts behind a spinner.
    const permanent = isPermanentVoiceError(err);
    const terminal = permanent || attempt >= MAX_RETRIES;
    const duration_ms = Date.now() - startedAt;
    // What the next person debugging this needs without asking the user.
    const recInfo = {
      bytes: typeof rec.audio_blob?.size === 'number' ? rec.audio_blob.size : null,
      mime: rec.audio_blob?.type || null,
      permanent,
    };
    await updateRecording(rec.recording_id, {
      status: terminal ? 'error' : 'queued',
      retry_count: attempt,
      next_retry_at: terminal ? null : Date.now() + backoffMs(attempt),
      error_message: err?.message || String(err),
    });
    logVoiceQueueEvent({
      event: 'upload_error',
      recording_id: rec.recording_id,
      scope_type: rec.scope_type,
      scope_id: rec.scope_id,
      duration_ms,
      attempt,
      error: err,
      ...recInfo,
    });
    logVoiceQueueEvent({
      event: terminal ? 'abandon' : 'retry',
      recording_id: rec.recording_id,
      scope_type: rec.scope_type,
      scope_id: rec.scope_id,
      duration_ms,
      attempt,
      error: err,
      ...recInfo,
    });
    return { ok: false, terminal, attempt };
  }
}

export async function tickOnce({
  processFn,
  concurrency = DEFAULT_CONCURRENCY,
  inFlight = moduleInFlight,
  inFlightIds = moduleInFlightIds,
} = {}) {
  if (!processFn) throw new Error('[queueWorker] tickOnce: processFn required');
  if (inFlight.size >= concurrency) {
    return { picked: 0, inFlight: inFlight.size };
  }
  const queued = await selectQueued();
  let picked = 0;
  while (inFlight.size < concurrency && queued.length > 0) {
    const rec = queued.shift();
    // Double-fire guard: never start a second concurrent upload of a recording
    // already in flight (e.g. rescueStaleUploading re-queued a slow-but-healthy
    // large upload). Skip now; it re-selects on a later tick once the in-flight
    // upload settles and the id frees.
    if (inFlightIds.has(rec.recording_id)) continue;
    inFlightIds.add(rec.recording_id);
    const job = processOne(rec, processFn);
    inFlight.add(job);
    job.finally(() => { inFlight.delete(job); inFlightIds.delete(rec.recording_id); });
    picked++;
  }
  return { picked, inFlight: inFlight.size };
}

function attachLifecycle() {
  if (typeof window === 'undefined') return;
  window.addEventListener('pagehide', stopWorker);
  window.addEventListener('beforeunload', stopWorker);
}

function detachLifecycle() {
  if (typeof window === 'undefined') return;
  window.removeEventListener('pagehide', stopWorker);
  window.removeEventListener('beforeunload', stopWorker);
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function startWorker({
  processFn,
  concurrency = DEFAULT_CONCURRENCY,
  pollMs = DEFAULT_POLL_MS,
  lockName = DEFAULT_LOCK_NAME,
} = {}) {
  if (!processFn) throw new Error('[queueWorker] startWorker: processFn required');
  if (!hasWebLocks()) {
    // Decision (TICKET-CODEX-3172673720): Block voice intake + surface an error
    // rather than fall back to a setInterval drain. A setInterval fallback risks
    // concurrent processing across tabs (the very problem Web Locks prevent), so
    // it would re-introduce the 2026-06-02 duplicate-upload incident on any
    // non-secure or legacy-browser context. Surfacing the error is safer: the CEO
    // sees a clear message, and no uploads are silently lost — they stay 'queued'
    // in IndexedDB until the user opens a supported browser.
    logVoiceQueueEvent({ event: 'worker_unavailable', reason: 'no-web-locks' });
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent('acn:voice-queue-unavailable', {
        detail: { reason: 'no-web-locks' },
      }));
    }
    return { isLeader: false, reason: 'no-web-locks' };
  }
  if (leaderActive) return { isLeader: true, reason: 'already-running' };

  stopFlag = false;
  return new Promise((resolveStart) => {
    let resolved = false;
    leaderPromise = navigator.locks.request(
      lockName,
      { mode: 'exclusive', ifAvailable: true },
      async (lock) => {
        if (!lock) {
          if (!resolved) {
            resolved = true;
            resolveStart({ isLeader: false, reason: 'lock-held-elsewhere' });
          }
          return;
        }
        leaderActive = true;
        attachLifecycle();
        if (!resolved) {
          resolved = true;
          resolveStart({ isLeader: true });
        }
        try {
          let tickFailing = false;
          while (!stopFlag) {
            // A rejected IndexedDB read (WebKit "Attempt to get records from
            // database without an in-progress transaction" after backgrounding)
            // must not exit the loop. Drop the cached connection so the next
            // tick reopens a fresh one, and log only the first failure of a
            // streak so a dead connection cannot write a row every poll.
            try {
              await tickOnce({ processFn, concurrency, inFlight: moduleInFlight });
              tickFailing = false;
            } catch (err) {
              // spine-emit-ok: logVoiceQueueEvent emits voice_queue:tick_error via logAppError
              if (!tickFailing) logVoiceQueueEvent({ event: 'tick_error', error: err });
              tickFailing = true;
              _resetDB();
            }
            await sleep(pollMs);
          }
          await Promise.allSettled(Array.from(moduleInFlight));
        } finally {
          detachLifecycle();
          leaderActive = false;
          leaderPromise = null;
        }
      }
    );
  });
}

export function stopWorker() {
  stopFlag = true;
}

export function isWorkerRunning() {
  return leaderActive && !stopFlag;
}

export function _resetWorkerState() {
  stopFlag = false;
  leaderActive = false;
  leaderPromise = null;
  moduleInFlight.clear();
  moduleInFlightIds.clear();
}

export const _internal = { backoffMs, isOnBackoff, selectQueued, processOne };
